import { useEffect, useState } from "react";
import axios from "axios";
import AdminMarkdownEditor from "./AdminMarkdownEditor";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminPageEditor({ editor = "public" }) {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null); // slug of selected page

    const [newSlug, setNewSlug] = useState("");
    const [newTitle, setNewTitle] = useState("");

    const loadPages = async () => {
        setLoading(true);
        try {
            const token = await auth.currentUser.getIdToken();
            const headers = {
                Authorization: `Bearer ${token}`,
            };
            
            let res;
            if (editor === "public") {
                res = await axios.get("/api/content", { headers });
            } else {
                res = await axios.get("/api/member-content", { headers });
            }
            setPages(res.data);
        } catch (error) {
            console.error("Error loading pages:", error);
            setPages([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPages();
    }, [editor]);

    const goBack = () => {
        setSelected(null);
        loadPages();
    };

    const handleCreate = async () => {
        const slug = newSlug.trim().toLowerCase().replace(/[^a-z0-9\-_\/]/g, "-");

        if (!slug) {
            toast.error("Please enter a valid slug");
            return;
        }

        try {
            const token = await auth.currentUser.getIdToken();
            const headers = {
                Authorization: `Bearer ${token}`,
            };
            
            if (editor === "public") {
                await axios.put(`/api/content/${slug}`, {
                    title: newTitle.trim() || slug.replace(/[-_]/g, " "),
                    markdown: "",
                    image: "",
                    use_static: false
                }, { headers });
            } else {
                await axios.put(`/api/member-content/members/${slug}`, {
                    title: newTitle.trim() || slug.replace(/[-_]/g, " "),
                    markdown: ""
                }, { headers });
            }
            setSelected(slug); // open for editing
            setNewSlug("");
            setNewTitle("");
        } catch (err) {
            toast.error("Something went wrong");
        }
    };

    const handleDelete = async (slug) => {
        const ok = await confirmSwal({
            title: `Delete page /${slug}?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        try {
            const token = await auth.currentUser.getIdToken();
            const headers = {
                Authorization: `Bearer ${token}`,
            };
            
            if (editor === "public") {
                await axios.delete(`/api/content/${slug}`, { headers });
            } else {
                await axios.delete(`/api/member-content/members/${slug}`, { headers });
            }
            setPages(pages => pages.filter(p => p.slug !== slug));
            toast.info("Page deleted");
        } catch {
            toast.error("Something went wrong");
        }
    };

    if (loading) return <p>Loading pages…</p>;

    if (selected) {
        const p = pages.find(pg => pg.slug === selected);
        const pageUrl = editor === "public" ? `/${selected}` : `/members/${selected}`;
        
        return (
            <div className="">
                <button className="btn small-link mb-3 ms-0" onClick={goBack}>
                    &larr; Back to page list
                </button>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Editing page: {p?.title || selected}</h4>
                    <a 
                        href={pageUrl} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="small-link"
                    >
                        View Page ↗
                    </a>
                </div>
                <p className="text-body-secondary">Last updated: {new Date(p?.updated_at || Date.now()).toLocaleDateString()}, {new Date(p?.updated_at || Date.now()).toTimeString()}</p>
                <AdminMarkdownEditor slug={selected} editor={editor} />
            </div>
        );
    }

    return (
        <div className="container mt-3">
            {/* New Page Form */}
            <div className="mb-4 p-3 border rounded">
                <h5>Create a New <strong>{editor === "public" ? "Public" : "Member"}</strong> Page</h5>

                <div className="mb-3">
                    <label className="form-label fw-bold">Slug</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 'our-mission'"
                        value={newSlug}
                        onChange={e => setNewSlug(e.target.value)}
                    />
                    <div className="form-text">Appears at <code>/your-slug</code></div>
                </div>

                <div className="mb-3">
                    <label className="form-label fw-bold">Title</label>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Page Title"
                        value={newTitle}
                        onChange={e => setNewTitle(e.target.value)}
                    />
                </div>

                <button className="btn btn-admin" onClick={handleCreate}>
                    Create & Edit
                </button>
            </div>

            {/* Page List */}
            {pages.length === 0 ? (
                <p className="text-muted">No pages found.</p>
            ) : (
                <ul className="list-group">
                    {pages.map(p => (
                        <li key={p.slug}
                            className="list-group-item d-flex justify-content-between align-items-center"
                        >
                            <div
                                style={{ cursor: "pointer" }}
                                onClick={() => setSelected(p.slug)}
                            >
                                <strong>{p.title}</strong>
                                <span className="text-body-secondary ms-4">/{p.slug}</span>
                            </div>

                            <div className="d-flex align-items-center gap-3">

                                {!p.is_static && (
                                    <button
                                        className="btn btn-sm btn-outline-danger"
                                        title="Delete page"
                                        onClick={() => handleDelete(p.slug)}
                                    >
                                        &times;
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
